import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { ensureNamespaces } from '../../i18n';
import BrandLogo from '../shared/brandLogo/brandLogo';
import LanguageSelector from '../shared/languageSwitcher/languageSwitcher';
import { BREAKPOINTS } from '../../styles/constants';
import { getAppScrollRoot, handleLandingAnchorClick } from '../../utils/landingAnchorScroll';

import {
  HeaderActions,
  HeaderBrandWrap,
  HeaderNav,
  HeaderNavLink,
  LandingContainer,
  LandingHeaderBar,
  LandingHeaderShell,
} from './landingPage.styles';

const LANDING_HEADER_HEIGHT_VAR = '--landing-header-height';

const NAV_ITEMS = [
  { key: 'features', hash: '#features' },
  { key: 'faq', hash: '#faq' },
  { key: 'contacts', hash: '#contacts' },
];

function setLandingHeaderHeightVar(heightPx) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(LANDING_HEADER_HEIGHT_VAR, `${heightPx}px`);

  const scrollRoot = getAppScrollRoot();
  if (scrollRoot) {
    scrollRoot.style.scrollPaddingTop = `${heightPx + 8}px`;
  }
}

function clearLandingHeaderHeightVar() {
  if (typeof document === 'undefined') return;
  document.documentElement.style.removeProperty(LANDING_HEADER_HEIGHT_VAR);

  const scrollRoot = getAppScrollRoot();
  if (scrollRoot) {
    scrollRoot.style.scrollPaddingTop = '';
  }
}

export default function LandingHeaderSection({ anchorPrefix = '' }) {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const isDesktop = useMediaQuery({ minWidth: BREAKPOINTS.tabletMin });
  const headerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    ensureNamespaces(['landing']);
  }, []);

  useLayoutEffect(() => {
    const headerEl = headerRef.current;
    if (!isDesktop || !headerEl) {
      clearLandingHeaderHeightVar();
      return undefined;
    }

    const measure = () => {
      const height = Math.ceil(headerEl.getBoundingClientRect().height);
      setLandingHeaderHeightVar(height);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(headerEl);
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
      clearLandingHeaderHeightVar();
    };
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop) {
      setScrolled(false);
      return undefined;
    }

    const scrollRoot = getAppScrollRoot();
    if (!scrollRoot) return undefined;

    const onScroll = () => {
      setScrolled(scrollRoot.scrollTop > 4);
    };

    onScroll();
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollRoot.removeEventListener('scroll', onScroll);
  }, [isDesktop]);

  const headerContent = (
    <LandingHeaderShell ref={headerRef} data-landing-header $scrolled={scrolled} $fixed={isDesktop}>
      <LandingContainer>
        <LandingHeaderBar>
          <HeaderBrandWrap>
            <Link to="/" aria-label="Healthy Pet home" style={{ textDecoration: 'none', color: 'inherit' }}>
              <BrandLogo size="header" />
            </Link>
          </HeaderBrandWrap>

          <HeaderNav aria-label="Landing navigation">
            {NAV_ITEMS.map(({ key, hash }) => (
              <HeaderNavLink
                key={key}
                href={`${anchorPrefix}${hash}`}
                onClick={(event) =>
                  handleLandingAnchorClick(event, `${anchorPrefix}${hash}`, { navigate, anchorPrefix })
                }
              >
                {t(`nav.${key}`)}
              </HeaderNavLink>
            ))}
          </HeaderNav>

          <HeaderActions>
            <LanguageSelector width="90px" useCodeLabels />
          </HeaderActions>
        </LandingHeaderBar>
      </LandingContainer>
    </LandingHeaderShell>
  );

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  return (
    <>
      {isDesktop && portalTarget
        ? createPortal(headerContent, portalTarget)
        : !isDesktop
          ? headerContent
          : null}
    </>
  );
}
